/** ✕ close-without-changes for the CRM popup pickers (PR3 item 8).
 *
 * The three pickers (`CrmChecklistEditor`, `RefCellEditor`, `CrmRosterPicker`) stage every toggle
 * through `onValueChange` and used to offer only "Done" — so the only way out of an opened picker was
 * to commit whatever state you had left it in. ✕ discards back to the value the editor OPENED with.
 *
 * ⚠ **Two hosts, two discard mechanisms — and the restore call is made in both, deliberately.**
 *
 *  - The **grid** hosts these as AG-Grid popup cell editors. `onValueChange` only STAGES, into the
 *    React proxy (`CellEditorComponentProxy.updateValue`); the row is written from `getValue()` when
 *    editing stops. So `api.stopEditing(true)` — AG-Grid's cancel path — discards the staged value on
 *    its own. Note `props.stopEditing` cannot do this: its argument is `suppressNavigateAfterEdit`,
 *    not `cancel`, so calling it would COMMIT. The cancel flag only exists on the grid api.
 *  - The record **panel** owns the commit: `PopupEditor` stages into a ref and writes only when the
 *    editor calls `stopEditing`. It therefore passes `onCancelEdit`, which closes without committing.
 *
 * Neither host writes per toggle today, which makes the `onValueChange(initial)` below a no-op belt.
 * It is kept — and pinned by tests — because it is the only thing that makes ✕ correct for a host that
 * DOES commit per toggle (an autosaving inline form is a plausible next host, and it would be a silent
 * data-loss bug if this helper assumed staging). Order matters: restore first, then discard.
 */
import styles from './crm.module.css';

/** The subset of the editor's props ✕ needs. `api` is AG-Grid's (absent in the panel host, hence the
 *  optional call); `onCancelEdit` is the panel host's callback (absent in the grid). */
export interface EditorCancelHost {
  onValueChange: (value: unknown) => void;
  api?: { stopEditing?: (cancel?: boolean) => void };
  onCancelEdit?: () => void;
}

export function cancelEditor(host: EditorCancelHost, initial: unknown): void {
  host.onValueChange(initial);          // belt — see the module docstring
  if (host.onCancelEdit) host.onCancelEdit();
  else host.api?.stopEditing?.(true);   // AG-Grid: `true` = CANCEL, discarding the staged value
}

/** The ✕ affordance itself. One component so all three pickers get the same glyph, label and title —
 *  "Close" alone read as "save and close" in review, which is exactly what it does NOT do. */
export function EditorCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button className={styles.refEditorClose} title="Close without changes"
            aria-label="Close without changes" onClick={onClick}>✕</button>
  );
}
